import React from 'react'

export type ChipVariant = 'default' | 'selected'

export interface ChipProps {
  variant?: ChipVariant
  selected?: boolean
  onSelect?: () => void
  onDismiss?: () => void
  leftIcon?: React.ReactNode
  disabled?: boolean
  children: React.ReactNode
}

const variantClasses: Record<ChipVariant, string> = {
  default:
    'bg-neutral-0 border-neutral-300 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400',
  selected: 'bg-brand-100 border-brand-500 text-brand-700 hover:bg-brand-200',
}

function getChildrenText(children: React.ReactNode): string {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  return ''
}

export function Chip({
  variant = 'default',
  selected = false,
  onSelect,
  onDismiss,
  leftIcon,
  disabled = false,
  children,
}: ChipProps): React.ReactElement {
  const effectiveVariant: ChipVariant = selected ? 'selected' : variant

  const baseClasses = [
    'inline-flex items-center gap-2 font-sans text-sm font-medium',
    'px-3 py-1 rounded-full border transition-colors',
    variantClasses[effectiveVariant],
    disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
  ].join(' ')

  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    onDismiss?.()
  }

  const label = getChildrenText(children)
  const dismissLabel = label ? `Remove ${label}` : 'Remove'

  const content = (
    <>
      {leftIcon && <span aria-hidden="true">{leftIcon}</span>}
      {children}
      {onDismiss && (
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={handleDismiss}
          disabled={disabled}
          className="ms-1 inline-flex items-center justify-center leading-none"
          tabIndex={disabled ? -1 : undefined}
        >
          ✕
        </button>
      )}
    </>
  )

  if (onSelect) {
    return (
      <button
        type="button"
        className={baseClasses}
        aria-pressed={selected}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={disabled ? undefined : onSelect}
      >
        {content}
      </button>
    )
  }

  return (
    <span className={baseClasses} aria-disabled={disabled}>
      {content}
    </span>
  )
}
